import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';

import { ENV_CONFIG } from '@ps/core-config';
import {
  CombineSurveyTrafficReportParams,
  ProjectManager,
  SurveyTransactionStatus,
} from '@ps/survey-utils';
import { ProjectManagerMapper } from './mappers/project-manager.mapper';
import { SurveyBuyerSettingsMapper } from './mappers/survey-buyer-settings.mapper';
import {
  OperatorProjectManagersResponseDto,
  ProjectManagerDto,
} from '../dtos/project-manager.dto';
import { CombineSurveyTrafficReportDto } from '../dtos/reports.dto';
import { SurveyBuyerSettingsResponseDto } from '../dtos/survey-buyer-settings.dto';
import { CombinedSTPayloadMapper } from './mappers/combined-st-report.mapper';
import { SurveyBuyerSettings } from '../../state/survey-state.model';

@Injectable({ providedIn: 'root' })
export class SurveyReferenceApi {
  private readonly _env = inject(ENV_CONFIG);
  private readonly _http = inject(HttpClient);

  private readonly _baseUrl = this._env.pureSpectrum.url;

  public getBuyerManagers(
    companyId: number,
    apiUsers = false,
  ): Observable<ProjectManager[]> {
    const params = new HttpParams({ fromObject: { apiUsers } });
    return this._http
      .get<ProjectManagerDto[]>(
        `${this._baseUrl}/companies/${companyId}/users`,
        {
          params,
        },
      )
      .pipe(map((dtos) => new ProjectManagerMapper().fromDtoList(dtos)));
  }

  public getOperatorManagers(apiUsers = false): Observable<ProjectManager[]> {
    const params = new HttpParams({ fromObject: { apiUsers } });
    return this._http
      .get<OperatorProjectManagersResponseDto>(
        `${this._baseUrl}/operator-users`,
        { params },
      )
      .pipe(map(({ users }) => new ProjectManagerMapper().fromDtoList(users)));
  }

  public getTransactionStatuses(): Observable<SurveyTransactionStatus[]> {
    return this._http.get<SurveyTransactionStatus[]>(
      `${this._baseUrl}/survey-transaction-status`,
    );
  }

  public getCombinedSurveyTrafficReport(
    data: CombineSurveyTrafficReportParams,
  ): Observable<CombineSurveyTrafficReportDto> {
    const payload = new CombinedSTPayloadMapper().toPayload(data);
    return this._http.get<CombineSurveyTrafficReportDto>(
      `${this._baseUrl}/reports/surveys/traffic-report`,
      { params: new HttpParams({ fromObject: { ...payload } }) },
    );
  }

  public downloadCSTReport(filename: string): Observable<Blob> {
    return this._http.get(
      `${this._baseUrl}/reports/download-report/${filename}`,
      { responseType: 'blob' },
    );
  }

  public getBuyerSettings(buyerId: number): Observable<SurveyBuyerSettings> {
    return this._http
      .get<SurveyBuyerSettingsResponseDto>(
        `${this._baseUrl}/buyerSettings/${buyerId}`,
      )
      .pipe(
        map((response) =>
          new SurveyBuyerSettingsMapper().fromDto(response.buyer),
        ),
      );
  }
}
